/**
 * GradientButton — the single "AI authoring" affordance. Gradient from
 * `aq-ai-from` to `aq-ai-to` (tokens, never hex). Used by the policy intent
 * chooser AI row and the PolicyStudio header. Distinct on purpose: AI is the
 * only gradient in the system.
 *
 * Carries the same action contract as `<Button>`: pass `onAction` and it derives
 * `pending` from the promise, swallows presses while pending, and uses
 * `aria-disabled` so focus is not dropped for the length of the request.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from 'react';
import type { IconProps } from '@shared/icons';
import { Spinner } from '../Spinner';
import type { ActionState } from '../Button';

export type GradientButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  onClick?: () => void;
  leading?: ComponentType<IconProps>;
  size?: 'sm' | 'md';
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  children: ReactNode;
  /** Async handler. The button owns `idle -> pending -> settled | failed`. */
  onAction?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** Label shown while `onAction` is in flight. Defaults to `children`. */
  pendingLabel?: ReactNode;
  /** Called when `onAction` rejects or throws, so the caller can show why. */
  onActionError?: (error: unknown) => void;
  /** Dwell on the success confirmation, in ms. 0 skips it. */
  settleMs?: number;
};

const ICON_PX: Record<'sm' | 'md', number> = { sm: 12, md: 14 };

const DEFAULT_SETTLE_MS = 1400;

export const GradientButton = forwardRef<HTMLButtonElement, GradientButtonProps>(
  function GradientButton(
    {
      onClick,
      leading: Leading,
      size = 'sm',
      type = 'button',
      disabled = false,
      loading = false,
      children,
      className,
      onAction,
      pendingLabel,
      onActionError,
      settleMs = DEFAULT_SETTLE_MS,
      ...rest
    },
    ref
  ) {
    const [actionState, setActionState] = useState<ActionState>('idle');

    const alive = useRef(true);
    const runId = useRef(0);
    const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Re-armed in the effect body so a StrictMode double-invoke cannot leave it false.
    useEffect(() => {
      alive.current = true;
      return () => {
        alive.current = false;
        if (settleTimer.current !== undefined) clearTimeout(settleTimer.current);
      };
    }, []);

    const isPending = loading || actionState === 'pending';
    const iconPx = ICON_PX[size];
    const pad = size === 'md' ? 'px-3.5 py-2 text-aq-body' : 'px-3 py-1.5 text-aq-caption';
    const blocked = disabled || isPending;
    const label = isPending && pendingLabel !== undefined ? pendingLabel : children;

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (disabled || isPending) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClick?.();
        if (!onAction) return;

        const id = runId.current + 1;
        runId.current = id;
        if (settleTimer.current !== undefined) clearTimeout(settleTimer.current);
        setActionState('pending');

        void (async () => {
          try {
            await onAction(event);
            if (!alive.current || runId.current !== id) return;
            if (settleMs <= 0) {
              setActionState('idle');
              return;
            }
            setActionState('settled');
            settleTimer.current = setTimeout(() => {
              if (alive.current && runId.current === id) setActionState('idle');
            }, settleMs);
          } catch (error) {
            // A newer press owns the button; that run reports its own outcome.
            if (runId.current !== id) return;
            // Reported before the mount check: an optimistic update can unmount
            // the button before its own request fails.
            onActionError?.(error);
            if (!alive.current) return;
            setActionState('failed');
            if (settleMs > 0) {
              settleTimer.current = setTimeout(() => {
                if (alive.current && runId.current === id) setActionState('idle');
              }, settleMs);
            }
          }
        })();
      },
      [disabled, isPending, onAction, onActionError, onClick, settleMs]
    );

    return (
      <button
        ref={ref}
        type={type}
        onClick={handleClick}
        disabled={disabled || undefined}
        data-action-state={onAction ? actionState : undefined}
        aria-disabled={blocked || undefined}
        aria-busy={isPending || undefined}
        className={[
          'aq-ai-gradient shadow-aq-card inline-flex items-center gap-1.5 rounded-md font-semibold',
          'text-aq-ink-on transition-opacity hover:opacity-90',
          // Opacity is disabled-only; pending keeps the label readable.
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          isPending ? 'cursor-progress' : '',
          pad,
          className ?? '',
        ].join(' ')}
        {...rest}
      >
        {/* One glyph position for the lifecycle, same as Button: the leading
            icon at rest, the spinner while pending. */}
        {isPending ? (
          <Spinner size={iconPx} />
        ) : Leading ? (
          <Leading size={iconPx} stroke={1.8} />
        ) : null}
        {label}
      </button>
    );
  }
);
